import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { alumniAPI } from '../../services/api';
import { FaArrowLeft, FaGraduationCap, FaUpload, FaFileAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Button from '../../components/Button';

const ApplyAlumni = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [existingApplication, setExistingApplication] = useState(null);
    const [checking, setChecking] = useState(true);
    const [formData, setFormData] = useState({
        course_completion_certificate_path: '',
        employment_status: 'employed',
        current_company: '',
        current_position: ''
    });
    const [certificateFile, setCertificateFile] = useState(null);

    useEffect(() => {
        checkExistingApplication();
    }, []);

    const checkExistingApplication = async () => {
        try {
            const res = await alumniAPI.getMyApplication();
            if (res.application) {
                setExistingApplication(res.application);
                setFormData({
                    course_completion_certificate_path: res.application.course_completion_certificate_path || '',
                    employment_status: res.application.employment_status || 'employed',
                    current_company: res.application.current_company || '',
                    current_position: res.application.current_position || ''
                });
            }
        } catch (err) {
            // No existing application
            console.log('No existing application found');
        } finally {
            setChecking(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCertificateFile(file);
            // For now, we'll use a placeholder path. In production, you'd upload to a file storage service
            setFormData({
                ...formData,
                course_completion_certificate_path: file.name
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!certificateFile && !formData.course_completion_certificate_path) {
            toast.error('Please upload your course completion certificate');
            return;
        }

        setLoading(true);

        try {
            // TODO: Upload file to server first, then use the returned path
            // For now, using file name as placeholder
            const applicationData = {
                ...formData,
                course_completion_certificate_path: certificateFile ? certificateFile.name : formData.course_completion_certificate_path
            };

            await alumniAPI.apply(applicationData);
            toast.success('Alumni application submitted successfully! It will be reviewed by the student manager.');
            navigate('/dashboard');
        } catch (err) {
            console.error('Failed to submit application:', err);
            toast.error(err.message || 'Failed to submit alumni application. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (checking) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (existingApplication && existingApplication.application_status === 'pending') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    <Link to="/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                        <FaArrowLeft className="mr-2" /> Back to Dashboard
                    </Link>

                    <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                        <FaFileAlt className="text-6xl text-yellow-500 mx-auto mb-4" />
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">Application Already Submitted</h1>
                        <p className="text-gray-600 mb-6">
                            You have a pending alumni application. Please wait for review by the student manager.
                        </p>
                        <p className="text-gray-500 text-sm mb-6">
                            Status: <span className="font-semibold text-yellow-600">{existingApplication.application_status}</span>
                        </p>
                        <Button to="/dashboard" variant="primary-gradient" className="inline-block px-6 py-3">
                            Go to Dashboard
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (existingApplication && existingApplication.application_status === 'approved') {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    <Link to="/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                        <FaArrowLeft className="mr-2" /> Back to Dashboard
                    </Link>

                    <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                        <FaGraduationCap className="text-6xl text-green-500 mx-auto mb-4" />
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">Congratulations!</h1>
                        <p className="text-gray-600 mb-6">
                            Your alumni application has been approved. You now have alumni status.
                        </p>
                        <Button to="/dashboard" variant="primary-gradient" className="inline-block px-6 py-3">
                            Go to Dashboard
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-4xl mx-auto px-6 py-8">
                <Link to="/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
                    <FaArrowLeft className="mr-2" /> Back to Dashboard
                </Link>

                <div className="bg-white rounded-xl shadow-lg p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <FaGraduationCap className="text-3xl text-primary-600" />
                        <h1 className="text-3xl font-bold text-gray-900">Apply for Alumni Status</h1>
                    </div>

                    <p className="text-gray-600 mb-6">
                        Complete the form below to apply for alumni status. Your application will be reviewed by the student manager.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Course Completion Certificate *
                            </label>
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-600 transition-colors">
                                <div className="space-y-1 text-center">
                                    <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                                    <div className="flex text-sm text-gray-600">
                                        <label htmlFor="certificate-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                                            <span>Upload a file</span>
                                            <input
                                                id="certificate-upload"
                                                name="certificate-upload"
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={handleFileChange}
                                                className="sr-only"
                                                required
                                            />
                                        </label>
                                        <p className="pl-1">or drag and drop</p>
                                    </div>
                                    <p className="text-xs text-gray-500">PDF, PNG, JPG up to 10MB</p>
                                    {certificateFile && (
                                        <p className="text-sm text-green-600 mt-2">
                                            Selected: {certificateFile.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Employment Status *
                            </label>
                            <select
                                required
                                value={formData.employment_status}
                                onChange={(e) => setFormData({...formData, employment_status: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                            >
                                <option value="employed">Employed</option>
                                <option value="self_employed">Self Employed</option>
                                <option value="freelancing">Freelancing</option>
                                <option value="unemployed">Unemployed</option>
                                <option value="student">Student</option>
                            </select>
                        </div>

                        {formData.employment_status !== 'unemployed' && formData.employment_status !== 'student' && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Current Company *
                                    </label>
                                    <input
                                        type="text"
                                        required={formData.employment_status !== 'unemployed' && formData.employment_status !== 'student'}
                                        value={formData.current_company}
                                        onChange={(e) => setFormData({...formData, current_company: e.target.value})}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                                        placeholder="Enter company name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Current Position *
                                    </label>
                                    <input
                                        type="text"
                                        required={formData.employment_status !== 'unemployed' && formData.employment_status !== 'student'}
                                        value={formData.current_position}
                                        onChange={(e) => setFormData({...formData, current_position: e.target.value})}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                                        placeholder="Enter your job title/position"
                                    />
                                </div>
                            </>
                        )}

                        <div className="flex gap-4 pt-4">
                            <Button type="submit" variant="primary-gradient" className="flex-1 px-6 py-3">
                                {loading ? 'Submitting...' : 'Submit Application'}
                            </Button>
                            <button
                                type="button"
                                onClick={() => navigate('/dashboard')}
                                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ApplyAlumni;

